import { useMutation, useQuery } from '@apollo/client';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import * as React from 'react';
import { useState } from 'react';
import { FaPlus, FaTrashAlt } from 'react-icons/fa';
import { Button } from 'reactstrap';
import { EditItem, EditSection } from '../../components/Layout';
import { updatePartnerSelfSignupConfigMutation } from '../../graphql/mutations';
import { partnerSelfSignupConfigsQuery } from '../../graphql/queries';
import { centsToPrice } from '@/utils/formatter';
// @ts-ignore
import nestedProperty from 'nested-property';

import {
  OnboardingProductGqlType,
  PartnerSelfSignupConfigGqlType,
  PartnerSelfSignupConfigUpdateGqlType,
  ProductPurchaseGqlType,
  ProductRentalGqlType,
  ProductTransactionsGqlType,
} from './PartnerSelfSignupListView';

type ArrayName =
  | 'products_transactions'
  | 'products_purchase'
  | 'products_rental'
  | 'products_onboarding';

interface IUrlParameters {
  match: {
    params: {
      configId: string;
    };
  };
}

export default function SelfSignupConfigDetailView(urlParams: IUrlParameters) {
  const { configId } = urlParams.match.params;
  const [selfSignupConfig, setSelfSignupConfig] = useState<
    PartnerSelfSignupConfigGqlType | undefined
  >(undefined);
  const [updateConfigMutation, { loading: updating }] = useMutation(
    updatePartnerSelfSignupConfigMutation,
  );
  const [isLoading, setIsLoading] = useState(true);

  useQuery(partnerSelfSignupConfigsQuery, {
    onCompleted: (data) => {
      setSelfSignupConfig(
        data.getPartnerSelfSignUpConfigs.find((c: any) => c.id === configId),
      );
      setIsLoading(false);
    },
  });

  if (isLoading) {
    return <p>loading</p>;
  }

  // @ts-ignore
  function handleChange({ target }) {
    let { name, value } = target;

    // only allow numbers if it is a number field
    if (name.endsWith('_cent') || name.endsWith('_number')) {
      value = value.replace(/[^0-9]/g, '');
    }

    setSelfSignupConfig((config: any) => {
      const newConfig = { ...config };
      if (config.billing_address) {
        newConfig.billing_address = { ...config.billing_address };
      }
      nestedProperty.set(newConfig, name, value);
      return newConfig;
    });
  }

  function handleChangeArrayItem(
    arrayName: ArrayName,
    itemName: string,
    attributeName: string,
    value: string,
  ) {
    setSelfSignupConfig((config: any) => {
      const newConfig = { ...config };
      const array = [...config[arrayName]];
      const index = array.findIndex((i: any) => i.name === itemName);
      const item = { ...array[index] };
      // only allow numbers if it is a number field
      if (
        attributeName.endsWith('_cent') ||
        attributeName.endsWith('_number') ||
        attributeName === 'count'
      ) {
        value = value.replace(/[^0-9]/g, '');
      }
      item[attributeName] = value;
      array[index] = item;
      newConfig[arrayName] = array;
      return newConfig;
    });
  }

  function removeFromArray(name: string, arrayName: ArrayName) {
    if (selfSignupConfig != null && selfSignupConfig[arrayName] != null) {
      const config = { ...selfSignupConfig };
      const array = [...(config[arrayName] || [])];
      const index = array.findIndex((e) => e.name === name);
      if (index === -1) {
        alert(`Error finding item with name '${name}'`);
      }
      array.splice(index, 1);
      //@ts-ignore
      config[arrayName] = array;
      setSelfSignupConfig(config);
    }
  }

  function addToArray(item: any, arrayName: ArrayName) {
    if (selfSignupConfig != null) {
      const config = { ...selfSignupConfig };
      const array = [...(config[arrayName] || [])];
      array.push(item);
      //@ts-ignore
      config[arrayName] = array;
      setSelfSignupConfig(config);
    }
  }

  function renderDelete(name: string, arrayName: ArrayName) {
    return (
      <Button
        className="ms-2"
        color="danger"
        size="sm"
        onClick={() => removeFromArray(name, arrayName)}
      >
        <FaTrashAlt />
      </Button>
    );
  }

  function save() {
    if (selfSignupConfig != null) {
      const { id, ...update } = { ...selfSignupConfig };
      const cleanObject = (key: string, value: string) => {
        if (key === '__typename' || key === 'signup_code') {
          return undefined;
        }
        if (
          key.endsWith('_cent') ||
          key.endsWith('_number') ||
          key === 'count'
        ) {
          const parsed = parseInt(value);
          return Number.isNaN(parsed) ? 0 : parsed;
        }
        return value;
      };
      const payload = JSON.parse(JSON.stringify(update), cleanObject);
      console.log(JSON.stringify(payload));
      updateConfigMutation({
        variables: {
          id: selfSignupConfig.id,
          update: payload,
        },
      });
    }
  }

  function renderInput(
    value: any,
    name: string,
    itemName: string,
    arrayName: ArrayName,
  ) {
    return (
      <input
        type="text"
        className="form-control"
        name={name}
        onChange={(e) =>
          handleChangeArrayItem(arrayName, itemName, name, e.target.value)
        }
        value={value}
      />
    );
  }

  function tableHeader(arrayName: ArrayName) {
    let item = {};
    if (arrayName === 'products_transactions') {
      const transaction: ProductTransactionsGqlType = {
        article_number: 123456,
        gtin: '123456',
        name: 'New Item',
        price_cent: 0,
      };
      item = transaction;
    } else if (arrayName === 'products_rental') {
      const rental: ProductRentalGqlType = {
        name: 'New Item',
        price_cent: 0,
      };
      item = rental;
    } else if (arrayName === 'products_purchase') {
      const purchase: ProductPurchaseGqlType = {
        article_number: 123456,
        gtin: '123456',
        name: 'New Item',
        price_cent: 0,
      };
      item = purchase;
    } else if (arrayName === 'products_onboarding') {
      const onboardingProduct: OnboardingProductGqlType = {
        article_number: 123456,
        gtin: '123456',
        name: 'New Item',
        count: 10,
      };
      item = onboardingProduct;
    }
    return (
      <div className="align-items-center export-buttons flex">
        <Button
          type="button"
          onClick={() => addToArray(item, arrayName)}
          className="p-button-success me-2"
        >
          <FaPlus />
        </Button>
      </div>
    );
  }

  function renderTable(arrayName: ArrayName, title: string) {
    return (
      <EditItem title={title}>
        <DataTable
          header={tableHeader(arrayName)}
          value={(selfSignupConfig || {})[arrayName] || []}
          dataKey="id"
          lazy
          loading={isLoading}
          className="p-datatable-striped"
          style={{ width: '100%', overflowX: 'scroll' }}
        >
          <Column
            headerStyle={{ width: '150px' }}
            body={(c: any) => renderInput(c.name, 'name', c.name, arrayName)}
            header="Name"
          />
          {arrayName !== 'products_onboarding' && (
            <Column
              headerStyle={{ width: '150px' }}
              body={(c: ProductTransactionsGqlType) =>
                renderInput(c.price_cent, 'price_cent', c.name, arrayName)
              }
              header="Price in Cent"
            />
          )}
          {arrayName !== 'products_onboarding' && (
            <Column
              headerStyle={{ width: '150px' }}
              body={(c: ProductTransactionsGqlType) => (
                <span>{centsToPrice(c.price_cent)}</span>
              )}
              header="Price"
            />
          )}
          {arrayName !== 'products_rental' && (
            <Column
              headerStyle={{ width: '150px' }}
              body={(c: ProductTransactionsGqlType) =>
                renderInput(
                  c.article_number,
                  'article_number',
                  c.name,
                  arrayName,
                )
              }
              header="Article Number"
            />
          )}
          {arrayName !== 'products_rental' && (
            <Column
              headerStyle={{ width: '150px' }}
              body={(c: ProductTransactionsGqlType) =>
                renderInput(c.gtin, 'gtin', c.name, arrayName)
              }
              header="GTIN"
            />
          )}
          {arrayName === 'products_onboarding' && (
            <Column
              headerStyle={{ width: '150px' }}
              body={(c: OnboardingProductGqlType) =>
                renderInput(c.count, 'count', c.name, arrayName)
              }
              header="Count"
            />
          )}
          <Column
            headerStyle={{ width: '75px' }}
            body={(c: ProductTransactionsGqlType) =>
              renderDelete(c.name, arrayName)
            }
            header="Delete"
          />
        </DataTable>
      </EditItem>
    );
  }

  const selfSignupType = (selfSignupConfig || {}).type;
  const billingAddress = (selfSignupConfig || {}).billing_address;

  return (
    <EditSection
      title="Self Signup Config"
      isLoading={isLoading || updating}
      onSave={save}
    >
      <EditItem title={'Details'}>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            Company Name
          </span>
          <input
            type="text"
            className="form-control"
            name="company_name"
            onChange={handleChange}
            value={(selfSignupConfig || {}).company_name || ''}
          />
        </div>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            Signup Code
          </span>
          <input
            type="text"
            className="form-control"
            name="signup_code"
            disabled
            value={(selfSignupConfig || {}).signup_code || ''}
          />
        </div>
        {(selfSignupConfig || {}).type === 'KAM' && (
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              KAM cancelation terms
            </span>
            <input
              type="text"
              className="form-control"
              name="cancelation_terms"
              onChange={handleChange}
              placeholder="three days..."
              value={(selfSignupConfig || {}).cancelation_terms || ''}
            />
          </div>
        )}
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            Onboarding Fee Cents
          </span>
          <input
            type="text"
            className="form-control"
            name="onboarding_fee_cent"
            onChange={handleChange}
            value={(selfSignupConfig || {}).onboarding_fee_cent || ''}
          />
          <span className="input-group-text">
            {' '}
            {centsToPrice(
              parseInt((selfSignupConfig || {}).onboarding_fee_cent as any),
            )}
          </span>
        </div>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            Type
          </span>
          <select
            className="form-control"
            name="type"
            onChange={handleChange}
            value={(selfSignupConfig || {}).type || ''}
          >
            <option>CATERER</option>
            <option>KAM</option>
          </select>
        </div>
      </EditItem>
      <EditItem title={'Billing address'}>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            Street
          </span>
          <input
            type="text"
            className="form-control"
            name="billing_address.street"
            onChange={handleChange}
            value={billingAddress?.street || ''}
          />
        </div>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            Zipcode
          </span>
          <input
            type="text"
            className="form-control"
            name="billing_address.zipcode"
            onChange={handleChange}
            value={billingAddress?.zipcode || ''}
          />
        </div>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            City
          </span>
          <input
            type="text"
            className="form-control"
            name="billing_address.city"
            onChange={handleChange}
            value={billingAddress?.city || ''}
          />
        </div>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            Country
          </span>
          <select
            className="form-control"
            name="billing_address.country"
            onChange={handleChange}
            value={billingAddress?.country || ''}
          >
            <option></option>
            <option value="DE">DE</option>
          </select>
        </div>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            State
          </span>
          <input
            type="text"
            className="form-control"
            name="billing_address.state"
            onChange={handleChange}
            value={billingAddress?.state || ''}
          />
        </div>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            Invoice Email
          </span>
          <input
            type="text"
            className="form-control"
            name="billing_address.invoice_email"
            onChange={handleChange}
            value={billingAddress?.invoice_email || ''}
          />
        </div>
        {selfSignupType === 'CATERER' && (
          <>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                POI box (CATERER)
              </span>
              <input
                type="text"
                className="form-control"
                name="billing_address.poi_box"
                onChange={handleChange}
                value={billingAddress?.poi_box || ''}
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                cost center (CATERER)
              </span>
              <input
                type="text"
                className="form-control"
                name="billing_address.cost_center"
                onChange={handleChange}
                value={billingAddress?.cost_center || ''}
              />
            </div>
          </>
        )}
      </EditItem>

      {selfSignupType === 'KAM' &&
        renderTable('products_onboarding', 'KAM Onboarding Products')}
      {renderTable('products_transactions', 'Transaction Products')}
      {renderTable('products_rental', 'Rental Products')}
      {renderTable('products_purchase', 'Purchase Products')}
    </EditSection>
  );
}
